import { useIntl } from "react-intl";
import {
  ButtonLink,
  ButtonLinkType,
  OptDecision,
  PurchaseLink,
} from "../../../../common/api/decimal/methods/course.ts";
import useChangeContentDecision from "./useChangeContentDecision.tsx";
import { CourseMaterial, useProgramDetails } from "./coursesLoader.ts";
import { useLayoutData } from "../layout/myCoursesLayoutLoader.ts";
import { optedOutWithoutBook, optedOutWithBook } from "./printStatusHelper.ts";
import useVirtualStore from "../../common/virtual-store/useVirtualStore.tsx";
import GlobalNavCart from "../../../../common/icons/GlobalNavCart.tsx";
import { getReaderNavURL } from "../../../bookshelfLinks.ts";
import useMaterialOptDeadline from "./useMaterialOptDeadline.tsx";
import { isMaterialInStoreCart } from "./common/materialStoreProductUtils.ts";
import { useMiniPdpContext } from "../../common/product/mini-pdp/miniPdpContext.ts";
import useOrderActions, { OrderButtonLinkType } from "./useOrderActions.tsx";
import MiscellaneousBook from "../../../../common/icons/MiscellaneousBook.tsx";

export interface Action extends BaseAction {
  ariaLabel: string;
}
export interface BaseAction {
  key: string; // used for focus management between different actions with same key
  label: string;
  onClick?: () => void | Promise<void>;
  href?: string;
  to?: string;
  category:
    | "access"
    | "favoredPurchase"
    | "info"
    | "illFavoredPurchase"
    | "details";
  type:
    | ButtonLinkType
    | OptDecision
    | OrderButtonLinkType
    | "view-details"
    | "confirm_print_pickup";
  meta?: "compare" | "mini-pdp" | "main-pdp";
  // todo reconsider meta in relation to lonestar changes
  icon?: JSX.Element;
}

const order = {
  access: 1,
  favoredPurchase: 2,
  info: 3,
  illFavoredPurchase: 4,
  details: 5,
};

// Methods that are promises will show loading dots when clicked
export interface Methods {
  comparePurchaseOptions: () => void;
  confirmOptOutDialog: () => void;
  confirmOptInPrintDialog: () => void;
  viewDetails: () => void;
  launchBook: (path: string) => Promise<void>;
  launchPiCourseware: (path: string) => Promise<void>;
  coursesAccessCodeDialog: (url: string) => void;
  lmsLinkDialog: (url: string) => void;
  libraryAccessCodeDialog: (url: string) => void;
}

export default function useMaterialActions({
  courseMaterial,
  methods,
}: {
  courseMaterial: CourseMaterial;
  methods: Methods;
}) {
  const actions: Action[] = [];
  const { buttonLinks, purchaseLinks, printStatus } = courseMaterial;
  const { config } = useLayoutData();
  const intl = useIntl();
  const { contentOptIn } = useChangeContentDecision({
    courseMaterial,
  });
  const optDecisionKey = `opt_decision_${courseMaterial.id}`;
  const hasOptIn = purchaseLinks.some((link) => link.type === "opt_in");
  const { isVirtualStore } = useVirtualStore();
  const { scope, userEffectiveProgram } = useProgramDetails();
  const { optWindowOpen } = useMaterialOptDeadline(courseMaterial);
  const { storeCart } = useLayoutData();
  const isInStoreCart = isMaterialInStoreCart(
    courseMaterial,
    storeCart?.lineItems
  );
  const miniPdp = useMiniPdpContext();
  const pdpLink = miniPdp?.pdpLink ?? "";
  const pdpLinkSelected = miniPdp?.pdpLinkSelected ?? "";
  const hasProducts = !!courseMaterial.product?.length;
  const orderActions = useOrderActions({
    courseMaterial,
    methods,
  });

  const actionSort = () => {
    actions.sort(
      (a: BaseAction, b: BaseAction) => order[a.category] - order[b.category]
    );
    // view details should always be last unless it is the main action
    // other than ill favored purchase in which case it moves to top
    if (
      !actions.filter(
        (a) =>
          !(a.category === "illFavoredPurchase" || a.category === "details")
      ).length
    ) {
      const viewDetailsAction = actions.find((a) => a.category === "details");
      if (viewDetailsAction) {
        actions.splice(actions.indexOf(viewDetailsAction), 1);
        actions.unshift(viewDetailsAction);
      }
    }
  };

  const buildAriaLabel = (action: BaseAction) => {
    const description = `${action.label} - ${courseMaterial.title}`;
    const linkText = action.href
      ? intl.formatMessage({ defaultMessage: "(opens in a new window)" })
      : "";
    return `${description} ${linkText}`;
  };

  const pushAction = (action: BaseAction) => {
    actions.push({ ...action, ariaLabel: buildAriaLabel(action) });
  };

  const optInAction = (): BaseAction => ({
    key: optDecisionKey,
    label: intl.formatMessage({ defaultMessage: "Opt In" }),
    onClick: () => contentOptIn(),
    category: "favoredPurchase",
    type: "opt_in",
  });

  const optInPrintAction = (): BaseAction => {
    let label = null;
    if (optedOutWithoutBook(printStatus)) {
      label = intl.formatMessage({ defaultMessage: "Get Print Book" });
    } else if (optedOutWithBook(printStatus)) {
      label = intl.formatMessage({ defaultMessage: "Keep Print Book" });
    } else {
      return optInAction();
    }
    return {
      key: optDecisionKey,
      label,
      onClick: () => methods.confirmOptInPrintDialog(),
      category: "favoredPurchase",
      type: "confirm_print_pickup",
    };
  };

  const optOutAction = (): BaseAction => ({
    key: optDecisionKey,
    label: intl.formatMessage({ defaultMessage: "Opt Out" }),
    onClick: () => methods.confirmOptOutDialog(),
    category: "illFavoredPurchase",
    type: "opt_out",
  });

  const viewDetails = (): BaseAction => ({
    key: "view_details",
    label: intl.formatMessage({ defaultMessage: "View Details" }),
    onClick: () => methods.viewDetails(),
    category: "details",
    to: undefined,
    type: "view-details",
  });

  const viewMainPdpDetails = (): BaseAction => ({
    key: "view_main_pdp_details",
    label: intl.formatMessage({ defaultMessage: "View Details" }),
    onClick: undefined,
    category: "details",
    to: pdpLinkSelected,
    type: "view-details",
    meta: "main-pdp",
  });

  const storeAddToCartAction = (): BaseAction => ({
    key: "store_add_to_cart",
    label: intl.formatMessage({ defaultMessage: "Add to Cart" }),
    category: "favoredPurchase",
    type: "ePOS",
    meta: "mini-pdp",
    // note action is non-trivial and handled in the mini-pdp
  });

  const storePurchaseOptionsAction = (): BaseAction => ({
    key: "view_store_purchase_options",
    label: intl.formatMessage({
      defaultMessage: "View More Purchase Options",
    }),
    to: pdpLink,
    category: "illFavoredPurchase",
    type: "ePOS",
    meta: "main-pdp",
  });

  const viewPurchaseOptionsAction = (): BaseAction => ({
    key: "view_purchase_options",
    label: intl.formatMessage({
      defaultMessage: "View Purchase Options",
    }),
    to: pdpLink,
    category: "details",
    type: "ePOS",
    meta: "main-pdp",
  });

  const storeViewInCartAction = (): BaseAction => ({
    key: "store_view_in_cart",
    label: intl.formatMessage({
      defaultMessage: "View in Cart",
    }),
    category: "info",
    type: "ePOS",
    icon: <GlobalNavCart />,
    to: "/my-cart",
  });

  const getEPOSAction = (buttonLink: ButtonLink): BaseAction | null => {
    // epos is handled differently in virtual store compared to original FA
    if (isVirtualStore) {
      if (!isInStoreCart) {
        if (
          courseMaterial.materialStatus === "opted_out" &&
          scope !== "ea_only" &&
          optWindowOpen
        ) {
          // special case highlights opt in but offers main pdp
          // but not when material is not present in the store
          return hasProducts ? storePurchaseOptionsAction() : null;
        } else {
          return storeAddToCartAction();
        }
      } else {
        return storeViewInCartAction();
      }
    } else {
      return {
        key: buttonLink.type,
        label: intl.formatMessage({ defaultMessage: "Purchase Options" }),
        onClick: () => methods.comparePurchaseOptions(),
        category: hasOptIn ? "illFavoredPurchase" : "favoredPurchase",
        type: buttonLink.type,
        meta: "compare",
      };
    }
  };

  const getButtonLinkAction = (buttonLink: ButtonLink): BaseAction | null => {
    switch (buttonLink.type) {
      case "buy":
        return {
          key: buttonLink.type,
          label: intl.formatMessage({ defaultMessage: "Buy Now" }),
          href: buttonLink.link,
          category: "illFavoredPurchase",
          type: buttonLink.type,
        };
      case "ePOS":
        return getEPOSAction(buttonLink);
      case "launch":
        return {
          key: buttonLink.type,
          label: intl.formatMessage({ defaultMessage: "Open Book" }),
          icon: <MiscellaneousBook />,
          href: buttonLink.link,
          category: "access",
          type: buttonLink.type,
        };
      case "launch_book":
        return {
          key: buttonLink.type,
          label: intl.formatMessage({ defaultMessage: "Open Book" }),
          icon: <MiscellaneousBook />,
          onClick: () => methods.launchBook(buttonLink.link),
          category: "access",
          type: buttonLink.type,
        };
      case "open_book":
        return {
          key: buttonLink.type,
          label: intl.formatMessage({ defaultMessage: "Open Book" }),
          icon: <MiscellaneousBook />,
          href: getReaderNavURL(buttonLink.link),
          category: "access",
          type: buttonLink.type,
        };
      case "connexions_open_book":
        return {
          key: buttonLink.type,
          label: intl.formatMessage({ defaultMessage: "Open Instructions" }),
          href: getReaderNavURL(buttonLink.link),
          category: "access",
          type: buttonLink.type,
        };
      case "launch_bundle_courseware":
        return {
          key: buttonLink.type,
          label: intl.formatMessage({ defaultMessage: "Access Instructions" }),
          href: buttonLink.link,
          category: "access",
          type: buttonLink.type,
        };
      case "launch_pi_bundle_courseware":
        return {
          key: buttonLink.type,
          label: intl.formatMessage({ defaultMessage: "Access Instructions" }),
          onClick: () => methods.launchPiCourseware(buttonLink.link),
          category: "access",
          type: buttonLink.type,
        };
      case "lms_link":
        return {
          key: buttonLink.type,
          label: intl.formatMessage({ defaultMessage: "Access Instructions" }),
          onClick: () => methods.lmsLinkDialog(buttonLink.link),
          category: "access",
          type: buttonLink.type,
        };
      case "launch_bundle_reveal_code":
      case "launch_reveal_code":
        return {
          key: buttonLink.type,
          label: intl.formatMessage({ defaultMessage: "Access Instructions" }),
          onClick: () => methods.coursesAccessCodeDialog(buttonLink.link),
          category: "access",
          type: buttonLink.type,
        };
      case "connexions_reveal_code":
        return {
          key: buttonLink.type,
          label: intl.formatMessage({ defaultMessage: "View Code & Pin" }),
          onClick: () => methods.libraryAccessCodeDialog(buttonLink.link),
          category: "access",
          type: buttonLink.type,
        };
      case "external_link":
      case "launch_access_code":
        return {
          key: buttonLink.type,
          label: intl.formatMessage({ defaultMessage: "Open" }),
          href: buttonLink.link,
          category: "access",
          type: buttonLink.type,
        };
      case "launch_pi_access_code":
        return {
          key: buttonLink.type,
          label: intl.formatMessage({ defaultMessage: "Open" }),
          onClick: () => methods.launchPiCourseware(buttonLink.link),
          category: "access",
          type: buttonLink.type,
        };
      case "update_shipping":
        return {
          key: buttonLink.type,
          label: intl.formatMessage({
            defaultMessage: "Update Shipping Address",
          }),
          href: buttonLink.link,
          category: "info",
          type: buttonLink.type,
        };
      case "view_shipping":
        return {
          key: buttonLink.type,
          label: intl.formatMessage({
            defaultMessage: "Shipping and Tracking",
          }),
          href: buttonLink.link,
          category: "access",
          type: buttonLink.type,
        };
      default:
        return null;
    }
  };

  const getViewDetails = (isVirtualStoreItem: boolean) => {
    if (isVirtualStore) {
      if (
        courseMaterial.materialStatus === "opted_in" ||
        (courseMaterial.materialStatus === "opted_out" && optWindowOpen) ||
        courseMaterial.materialStatus === "included" ||
        courseMaterial.materialStatus === "continuation"
      ) {
        return viewDetails();
      } else if (courseMaterial.manageLicense?.isTrialAccess === false) {
        // no action
      } else if (isVirtualStoreItem) {
        return pdpLinkSelected ? viewMainPdpDetails() : null;
      } else {
        return viewDetails();
      }
    } else {
      return viewDetails();
    }
  };

  buttonLinks.forEach((buttonLink: ButtonLink) => {
    const action = getButtonLinkAction(buttonLink);
    if (action) {
      pushAction(action);
    }
  });

  purchaseLinks.forEach((purchaseLink: PurchaseLink) => {
    if (purchaseLink.type === "opt_in") {
      if (
        config.assets.showPrintPickup &&
        courseMaterial.materialFormat === "print"
      ) {
        pushAction(optInPrintAction());
      } else {
        pushAction(optInAction());
      }
    } else if (purchaseLink.type === "opt_out") {
      pushAction(optOutAction());
    }
  });

  orderActions.forEach((action) => pushAction(action));

  const miniPdpExists = !!actions.find((a) => a.meta === "mini-pdp");
  const isVirtualStoreItem = isVirtualStore && (miniPdpExists || isInStoreCart);

  const viewDetailsAction = getViewDetails(isVirtualStoreItem);
  if (viewDetailsAction) {
    pushAction(viewDetailsAction);

    if (
      isVirtualStore &&
      userEffectiveProgram === "no_program" &&
      courseMaterial.materialStatus === "opted_out" &&
      viewDetailsAction.key !== "view_main_pdp_details" &&
      isVirtualStoreItem &&
      pdpLink
    ) {
      pushAction(viewPurchaseOptionsAction());
    }
  }

  actionSort();
  return actions;
}
